import { CheckCircle as CheckCircleIcon, Warning as WarningIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { usePublishVersionMutation } from './price-management-api-slice';
import { PriceVersion } from './types';

export interface PublishVersionModalProps {
  open: boolean;
  version: PriceVersion;
  manufacturerName?: string;
  onClose: () => void;
  onSuccess: (publishedVersion: PriceVersion) => void;
}

/**
 * Publish Version Modal (US6)
 * Single-step dialog: version notes + confirmation checkbox → publish.
 */
const PublishVersionModal: React.FC<PublishVersionModalProps> = ({
  open,
  version,
  manufacturerName,
  onClose,
  onSuccess,
}) => {
  const [changeDescription, setChangeDescription] = useState(version.changeDescription ?? '');
  const [descriptionError, setDescriptionError] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const [publishVersion, { isLoading, error, reset }] = usePublishVersionMutation();

  const bundleCostCount = version.bundleCosts?.length ?? 0;
  const bomHardwareCount = version.bomHardware?.length ?? 0;

  // Track previous open state to detect open→true transition only
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      // Modal just opened — reset all fields
      setChangeDescription(version.changeDescription ?? '');
      setDescriptionError('');
      setConfirmed(false);
      reset();
    }
    prevOpenRef.current = open;
  });

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const validateDescription = (value: string) => {
    if (!value.trim()) return 'Change description is required.';
    if (value.trim().length < 10) return 'Must be at least 10 characters.';
    if (value.trim().length > 500) return 'Must be 500 characters or fewer.';
    return '';
  };

  const handlePublish = async () => {
    const err = validateDescription(changeDescription);
    if (err) {
      setDescriptionError(err);
      return;
    }
    if (!confirmed) return;

    try {
      const published = await publishVersion({
        versionId: version.id,
        rowVersion: Array.from(version.rowVersion ?? []),
        changeDescription: changeDescription.trim(),
      }).unwrap();
      onSuccess(published);
    } catch {
      // error shown via mutation `error` state
    }
  };

  const charCount = changeDescription.trim().length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isLoading}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6" component="span">
            Publish Version {version.versionNumber}.0
            {manufacturerName ? ` — ${manufacturerName}` : ''}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Warning banner */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            p: 2,
            mb: 2,
            bgcolor: 'warning.50',
            border: '1px solid',
            borderColor: 'warning.300',
            borderRadius: 1,
          }}
        >
          <WarningIcon color="warning" sx={{ flexShrink: 0, mt: 0.25 }} />
          <Box>
            <Typography variant="body2" fontWeight={700} color="warning.dark">
              IMPORTANT: This will make pricing active immediately
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All new orders will use this pricing immediately.
            </Typography>
          </Box>
        </Box>

        {/* Order impact */}
        <Box
          sx={{
            p: 2,
            mb: 2,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            📋 Order Impact
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              ✅{' '}
              <span>
                All new orders will use <strong>Version {version.versionNumber}.0</strong> pricing
              </span>
            </Typography>
            {(bundleCostCount > 0 || bomHardwareCount > 0) && (
              <Typography variant="body2" color="text.secondary" sx={{ pl: 3 }}>
                {bundleCostCount} bundle cost{bundleCostCount !== 1 ? 's' : ''} and{' '}
                {bomHardwareCount} BOM hardware item{bomHardwareCount !== 1 ? 's' : ''} will be
                published
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Version notes */}
        <TextField
          label="Change Description"
          required
          multiline
          rows={3}
          fullWidth
          value={changeDescription}
          onChange={e => {
            setChangeDescription(e.target.value);
            if (descriptionError) setDescriptionError(validateDescription(e.target.value));
          }}
          placeholder="e.g. Q1 2026 steel price increases from supplier"
          error={!!descriptionError}
          helperText={
            descriptionError ||
            `${charCount}/500 characters${charCount > 0 && charCount < 10 ? ` (${10 - charCount} more needed)` : ''}`
          }
          inputProps={{ maxLength: 500 }}
          sx={{ mb: 2 }}
        />

        {/* Confirmation checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={e => setConfirmed(e.target.checked)}
              color="primary"
            />
          }
          label={<Typography variant="body2">I confirm I want to publish this version</Typography>}
        />

        {error != null && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {typeof error === 'object' && 'data' in (error as object)
              ? String(
                  (error as { data: { title?: string } }).data?.title ??
                    (error as { data: unknown }).data
                )
              : 'An error occurred while publishing. Please try again.'}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handlePublish}
          disabled={!confirmed || isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CheckCircleIcon fontSize="small" />
            )
          }
        >
          {isLoading ? 'Publishing...' : 'Publish'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishVersionModal;
